import {
    keepPreviousData,
    useMutation,
    useQuery,
    useQueryClient,
} from "@tanstack/react-query";
import { useCallback } from "react";
import { ServerInferRequest } from "@ts-rest/core";
import {
    taskContract,
    TaskBulkUpdate,
    TaskListQuery,
    TaskTimeInStatusResponse,
} from "@common/contracts/task.contract";
import {
    TaskBulkUpdateAction,
    TaskThreadUnreadCount,
} from "@common/schemas/task.schema";
import {
    Task,
    TaskSchema,
    TaskSelectionSummary,
    TaskSelectionSummarySchema,
} from "@common/types/task";
import { AxiosError } from "axios";
import { useScope } from "@state/scopeContext.lib";
import { useViewOnlyRefusal } from "@hooks/permissions/useViewOnlyRefusal";
import { SUPPRESS_ERROR_TOAST_HEADER } from "@lib/backendClient";
import { isThreadUnread } from "@components/mainInterface/chat/threadUnread";
import {
    organizationTaskListsFilter,
    organizationTaskQueryKeys,
} from "./organizationTask.api";
import { taskClient } from "./taskClient";
import { updateTasksAcrossWorkspaces } from "./taskBulkUpdateAcrossWorkspaces";
import {
    invalidateTodoTaskCounts,
    invalidateWorkspaceTaskQueries,
    tasksQueryKeys,
    todoTasksCountQueryKey,
} from "./taskQueryKeys";
import {
    cancelTaskUpdateQueries,
    invalidateCloseHealthQueries,
    invalidateUpdatedTaskQueries,
    isTaskBulkUpdateApplied,
    requestTaskUpdate,
    restoreTaskUpdateSnapshot,
    type TaskBulkUpdateResponse,
    type TaskUpdateBody,
    writeOptimisticTaskUpdate,
} from "./taskUpdateCache";

export { tasksQueryKeys, todoTasksCountQueryKey } from "./taskQueryKeys";
export {
    cancelTaskUpdateQueries,
    invalidateCloseHealthQueries,
    invalidateUpdatedTaskQueries,
    isTaskBulkUpdateApplied,
    requestTaskUpdate,
    type OptimisticTaskFields,
    restoreTaskFromUpdateSnapshot,
    restoreTaskUpdateSnapshot,
    type TaskBulkUpdateResponse,
    type TaskUpdateBody,
    type TaskUpdateSnapshot,
    writeOptimisticTaskUpdate,
} from "./taskUpdateCache";

interface FetchTaskListParams {
    workspaceId: string;
    query?: TaskListQuery;
}

export async function fetchTaskList({
    workspaceId,
    query,
}: FetchTaskListParams): Promise<Task[]> {
    const response = await taskClient.listTasks({
        params: { workspaceId },
        query: query ?? {},
    });

    return response.body.tasks.map((task) => TaskSchema.parse(task));
}

interface FetchTaskThreadUnreadCountsParams {
    workspaceId: string;
    organizationId?: string | null;
    query?: TaskListQuery;
}

export async function fetchTaskThreadUnreadCounts({
    workspaceId,
    organizationId,
    query,
}: FetchTaskThreadUnreadCountsParams): Promise<TaskThreadUnreadCount[]> {
    const response = await taskClient.listTaskThreadUnreadCounts({
        params: { workspaceId },
        query: {
            ...query,
            organizationId: organizationId ?? undefined,
        },
    });

    return response.body.counts;
}

export function useTasks(query?: TaskListQuery) {
    const { workspaceId, activeWorkspaceKey } = useScope();

    return useQuery({
        queryKey: tasksQueryKeys.list(activeWorkspaceKey, query),
        queryFn: async () => {
            if (!workspaceId) {
                return undefined;
            }

            return await fetchTaskList({ workspaceId, query });
        },
        placeholderData: keepPreviousData,
    });
}

export function useTaskThreadUnreadCounts(query?: TaskListQuery) {
    const { workspaceId, organizationId, activeWorkspaceKey } = useScope();

    return useQuery({
        queryKey: tasksQueryKeys.threadUnreadCounts(
            activeWorkspaceKey,
            query,
            organizationId,
        ),
        queryFn: async () => {
            if (!workspaceId) {
                return [] as TaskThreadUnreadCount[];
            }

            return await fetchTaskThreadUnreadCounts({
                workspaceId,
                organizationId,
                query,
            });
        },
        placeholderData: keepPreviousData,
    });
}

interface UseTaskDetailOptions {
    relatedTask?: boolean;
}

export function useTaskDetail(taskId: string, options?: UseTaskDetailOptions) {
    const { workspaceId, activeWorkspaceKey } = useScope();

    return useQuery({
        queryKey: tasksQueryKeys.detail(activeWorkspaceKey, taskId),
        queryFn: async (): Promise<Task | null | undefined> => {
            if (!workspaceId) {
                return undefined;
            }

            try {
                const response = await taskClient.getTaskDetail({
                    params: { workspaceId, taskId },
                    ...(options?.relatedTask
                        ? {
                              headers: {
                                  [SUPPRESS_ERROR_TOAST_HEADER]: "true",
                              },
                          }
                        : {}),
                });

                return TaskSchema.parse(response.body);
            } catch (error) {
                const statusCode = (error as AxiosError)?.response?.status;
                if (options?.relatedTask && statusCode === 403) {
                    // Related tasks can be inaccessible due to permissions.
                    // Return null so UI can render a "No access" state.
                    return null;
                }

                throw error;
            }
        },
        enabled: !!workspaceId && !!taskId,
        staleTime: 1000 * 60,
    });
}

export function useTaskTimeInStatus(taskId: string) {
    const { workspaceId, activeWorkspaceKey } = useScope();

    return useQuery<TaskTimeInStatusResponse | undefined>({
        queryKey: tasksQueryKeys.timeInStatus(activeWorkspaceKey, taskId),
        queryFn: async () => {
            if (!workspaceId) {
                return undefined;
            }

            const response = await taskClient.getTimeInStatus({
                params: { workspaceId, taskId },
            });

            return response.body;
        },
        enabled: !!workspaceId && !!taskId,
        staleTime: 1000 * 30,
    });
}

export function useTaskSummaries({
    enabled = true,
}: { enabled?: boolean } = {}) {
    const { workspaceId, activeWorkspaceKey } = useScope();

    return useQuery({
        queryKey: tasksQueryKeys.summaries(activeWorkspaceKey),
        enabled,
        queryFn: async (): Promise<TaskSelectionSummary[] | undefined> => {
            if (!workspaceId) {
                return undefined;
            }

            const response = await taskClient.listTaskSummaries({
                params: { workspaceId },
            });

            return response.body.tasks.map((taskSummary) =>
                TaskSelectionSummarySchema.parse(taskSummary),
            );
        },
        staleTime: 1000 * 60,
        placeholderData: keepPreviousData,
    });
}

export function useTodoTasksCount() {
    const { workspaceId, activeWorkspaceKey } = useScope();
    return useQuery({
        queryKey: todoTasksCountQueryKey(activeWorkspaceKey),
        queryFn: async () => {
            if (!workspaceId) {
                return 0;
            }
            const res = await taskClient.getNumberOfTodoTasks({
                params: { workspaceId },
            });
            return res.body.count;
        },
        enabled: !!workspaceId,
    });
}

export type TaskCreateBody = ServerInferRequest<
    typeof taskContract.createTask
>["body"];

interface CreateTaskCallbacks {
    /**
     * Runs after every create that succeeds, with the body it was called
     * with, even once another create started or the caller unmounted (unlike
     * the callbacks `mutate` takes, which only the latest call gets).
     */
    onSuccess?: (task: Task, body: TaskCreateBody) => void;
    /** Runs after every create that fails, as `onSuccess` does. */
    onError?: (body: TaskCreateBody) => void;
}

export function useCreateTask({
    onSuccess,
    onError,
}: CreateTaskCallbacks = {}) {
    const queryClient = useQueryClient();
    const { workspaceId, activeWorkspaceKey } = useScope();

    return useMutation({
        mutationFn: async (body: TaskCreateBody) => {
            if (!workspaceId) {
                throw new Error("No active workspace");
            }

            const res = await taskClient.createTask({
                params: { workspaceId },
                body,
            });

            return TaskSchema.parse(res.body);
        },
        onSuccess: (task, body) => {
            queryClient.invalidateQueries({
                queryKey: tasksQueryKeys.listByWorkspaceKey(activeWorkspaceKey),
                exact: false,
            });
            queryClient.invalidateQueries({
                queryKey: tasksQueryKeys.summaries(activeWorkspaceKey),
                exact: true,
            });
            invalidateTodoTaskCounts(queryClient, activeWorkspaceKey);
            onSuccess?.(task, body);
        },
        onError: (_error, body) => onError?.(body),
    });
}

export function useUpdateTask(task: Task) {
    const queryClient = useQueryClient();
    const { activeWorkspaceKey } = useScope();
    const { isViewOnly } = useViewOnlyRefusal();

    return useMutation({
        mutationFn: (body: TaskUpdateBody) => requestTaskUpdate({ task, body }),
        onMutate: async (payload) => {
            // View-only is refused by the API; an optimistic write would only flash.
            if (isViewOnly) {
                return;
            }

            const cacheArgs = {
                queryClient,
                workspaceKey: activeWorkspaceKey,
            };
            await cancelTaskUpdateQueries({ ...cacheArgs, taskId: task.id });
            return writeOptimisticTaskUpdate({ ...cacheArgs, task, payload });
        },
        onError: (_error, _payload, context) => {
            if (!context) {
                return;
            }
            restoreTaskUpdateSnapshot(queryClient, context);
        },
        onSettled: (_data, _error, body) => {
            // Always reconcile against server truth, even after an error
            // rollback, so any partial server-side change is picked up.
            invalidateUpdatedTaskQueries({
                queryClient,
                workspaceKey: activeWorkspaceKey,
                taskId: task.id,
            });
            invalidateCloseHealthQueries({ queryClient, task, body });
        },
    });
}

function useTaskDocumentIdsOptimisticUpdate(task: Task) {
    const queryClient = useQueryClient();
    const { activeWorkspaceKey } = useScope();
    const { isViewOnly } = useViewOnlyRefusal();

    return useCallback(
        async ({
            linkIds,
            unlinkIds,
        }: {
            linkIds?: number[];
            unlinkIds?: number[];
        }) => {
            // View-only is refused by the API; an optimistic write would only flash.
            if (isViewOnly) {
                return;
            }

            const listKey =
                tasksQueryKeys.listByWorkspaceKey(activeWorkspaceKey);
            const detailKey = tasksQueryKeys.detail(
                activeWorkspaceKey,
                task.id,
            );

            await queryClient.cancelQueries({
                queryKey: listKey,
                exact: false,
            });
            await queryClient.cancelQueries({
                queryKey: detailKey,
                exact: false,
            });

            const applyDocumentDelta = (current: Task): Task => {
                const currentIds =
                    current.financialDocumentIds ??
                    current.financialDocuments?.map((doc) => doc.id) ??
                    [];
                const linkSet = new Set(linkIds ?? []);
                const unlinkSet = new Set(unlinkIds ?? []);
                const nextIds = [
                    ...new Set([
                        ...currentIds.filter((id) => !unlinkSet.has(id)),
                        ...linkSet,
                    ]),
                ];
                const nextIdSet = new Set(nextIds);
                return {
                    ...current,
                    financialDocumentIds: nextIds,
                    financialDocuments: current.financialDocuments?.filter(
                        (doc) => nextIdSet.has(doc.id),
                    ),
                };
            };

            queryClient.setQueriesData(
                { queryKey: listKey, exact: false },
                (oldData: Task[] | undefined) => {
                    if (!oldData) {
                        return oldData;
                    }
                    return oldData.map((t) =>
                        t.id === task.id ? applyDocumentDelta(t) : t,
                    );
                },
            );

            queryClient.setQueryData(detailKey, (oldData: Task | undefined) => {
                if (!oldData) {
                    return oldData;
                }
                return applyDocumentDelta(oldData);
            });
        },
        [activeWorkspaceKey, isViewOnly, queryClient, task.id],
    );
}

function useInvalidateTaskQueries(task: Task) {
    const queryClient = useQueryClient();
    const { activeWorkspaceKey } = useScope();

    return useCallback(
        () =>
            invalidateUpdatedTaskQueries({
                queryClient,
                workspaceKey: activeWorkspaceKey,
                taskId: task.id,
            }),
        [activeWorkspaceKey, queryClient, task.id],
    );
}

export function useLinkTaskDocuments(task: Task) {
    const applyOptimisticUpdate = useTaskDocumentIdsOptimisticUpdate(task);
    const invalidateTaskQueries = useInvalidateTaskQueries(task);

    return useMutation({
        mutationFn: async (financialDocumentIds: number[]) => {
            const res = await taskClient.linkDocumentsToTask({
                params: { workspaceId: task.workspaceId, taskId: task.id },
                body: { financialDocumentIds },
            });
            return res.body.data.financialDocumentIds;
        },
        onMutate: async (financialDocumentIds) =>
            await applyOptimisticUpdate({ linkIds: financialDocumentIds }),
        onSettled: invalidateTaskQueries,
    });
}

export function useUnlinkTaskDocuments(task: Task) {
    const applyOptimisticUpdate = useTaskDocumentIdsOptimisticUpdate(task);
    const invalidateTaskQueries = useInvalidateTaskQueries(task);

    return useMutation({
        mutationFn: async (financialDocumentIds: number[]) => {
            const res = await taskClient.unlinkDocumentsFromTask({
                params: { workspaceId: task.workspaceId, taskId: task.id },
                body: { financialDocumentIds },
            });
            return res.body.data.financialDocumentIds;
        },
        onMutate: async (financialDocumentIds) =>
            await applyOptimisticUpdate({ unlinkIds: financialDocumentIds }),
        onSettled: invalidateTaskQueries,
    });
}

export function useBulkUpdateTasks() {
    const queryClient = useQueryClient();
    const { workspaceId, activeWorkspaceKey } = useScope();

    return useMutation({
        mutationFn: async (
            body: TaskBulkUpdate,
        ): Promise<TaskBulkUpdateResponse> => {
            if (!workspaceId) {
                throw new Error("No active workspace");
            }

            return await taskClient.bulkUpdateTasks({
                params: { workspaceId },
                body,
            });
        },
        onSuccess: (response) => {
            if (!isTaskBulkUpdateApplied(response)) {
                return;
            }

            queryClient.invalidateQueries({
                queryKey: tasksQueryKeys.listByWorkspaceKey(activeWorkspaceKey),
                exact: false,
            });
            queryClient.invalidateQueries({
                queryKey:
                    tasksQueryKeys.detailByWorkspaceKey(activeWorkspaceKey),
                exact: false,
            });
            queryClient.invalidateQueries({
                queryKey: tasksQueryKeys.summaries(activeWorkspaceKey),
                exact: true,
            });
            invalidateTodoTaskCounts(queryClient, activeWorkspaceKey);
        },
    });
}

interface BulkUpdateTasksAcrossWorkspacesParams {
    tasks: ReadonlyArray<Pick<Task, "id" | "workspaceId">>;
    action: TaskBulkUpdateAction;
}

/** Bulk update for the organization Tasks page, whose rows span client workspaces. */
export function useBulkUpdateTasksAcrossWorkspaces() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({
            tasks,
            action,
        }: BulkUpdateTasksAcrossWorkspacesParams) =>
            updateTasksAcrossWorkspaces({
                tasks,
                action,
                sendBatch: ({ workspaceId, taskIds, action: batchAction }) =>
                    taskClient.bulkUpdateTasks({
                        params: { workspaceId },
                        body: { taskIds, action: batchAction },
                    }),
            }),
        onSettled: (_result, _error, { tasks }) => {
            const workspaceIds = new Set(tasks.map((task) => task.workspaceId));
            for (const workspaceId of workspaceIds) {
                invalidateWorkspaceTaskQueries(queryClient, workspaceId);
            }
        },
    });
}

export function useBulkDeleteTasks() {
    const queryClient = useQueryClient();
    const { workspaceId, activeWorkspaceKey } = useScope();

    return useMutation({
        mutationFn: async (taskIds: string[]) => {
            if (!workspaceId) {
                throw new Error("No active workspace");
            }

            return await taskClient.bulkDeleteTasks({
                params: { workspaceId },
                body: { taskIds },
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: tasksQueryKeys.listByWorkspaceKey(activeWorkspaceKey),
                exact: false,
            });
            queryClient.invalidateQueries({
                queryKey:
                    tasksQueryKeys.detailByWorkspaceKey(activeWorkspaceKey),
                exact: false,
            });
            queryClient.invalidateQueries({
                queryKey: tasksQueryKeys.summaries(activeWorkspaceKey),
                exact: true,
            });
            invalidateTodoTaskCounts(queryClient, activeWorkspaceKey);
        },
    });
}

export function useDeleteTask(task: Task) {
    const queryClient = useQueryClient();
    const { activeWorkspaceKey } = useScope();
    const { isViewOnly } = useViewOnlyRefusal();

    return useMutation({
        mutationFn: async () => {
            await taskClient.deleteTask({
                params: { workspaceId: task.workspaceId, taskId: task.id },
            });
        },
        onMutate: async () => {
            // This one has no rollback: without the guard a refused delete
            // would drop the row from the list until the next refetch.
            if (isViewOnly) {
                return;
            }

            await queryClient.cancelQueries({
                queryKey: tasksQueryKeys.listByWorkspaceKey(activeWorkspaceKey),
                exact: false,
            });
            await queryClient.cancelQueries(organizationTaskListsFilter);
            await queryClient.cancelQueries({
                queryKey: tasksQueryKeys.summaries(activeWorkspaceKey),
                exact: true,
            });

            const removeTaskFromList = (oldData: Task[] | undefined) => {
                if (!oldData) {
                    return oldData;
                }
                return oldData.filter((t) => t.id !== task.id);
            };
            queryClient.setQueriesData(
                {
                    queryKey:
                        tasksQueryKeys.listByWorkspaceKey(activeWorkspaceKey),
                    exact: false,
                },
                removeTaskFromList,
            );
            queryClient.setQueriesData(
                organizationTaskListsFilter,
                removeTaskFromList,
            );

            queryClient.setQueryData(
                tasksQueryKeys.summaries(activeWorkspaceKey),
                (oldData: TaskSelectionSummary[] | undefined) => {
                    if (!oldData) {
                        return oldData;
                    }
                    return oldData.filter(
                        (taskSummary) => taskSummary.id !== task.id,
                    );
                },
            );

            queryClient.removeQueries({
                queryKey: tasksQueryKeys.detail(activeWorkspaceKey, task.id),
                exact: true,
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: tasksQueryKeys.listByWorkspaceKey(activeWorkspaceKey),
                exact: false,
            });
            queryClient.invalidateQueries({
                queryKey: tasksQueryKeys.summaries(activeWorkspaceKey),
                exact: true,
            });
            invalidateTodoTaskCounts(queryClient, activeWorkspaceKey);
        },
    });
}

export function useSetTaskRelatedTasks() {
    const queryClient = useQueryClient();
    const { activeWorkspaceKey } = useScope();

    return useMutation({
        mutationFn: async ({
            taskId,
            workspaceId,
            relatedTaskIds,
        }: {
            taskId: string;
            workspaceId: string;
            relatedTaskIds: string[];
        }) => {
            const res = await taskClient.updateTask({
                params: { workspaceId, taskId },
                body: { relatedTaskIds },
            });
            return TaskSchema.parse(res.body);
        },
        onSuccess: (task) => {
            queryClient.invalidateQueries({
                queryKey: tasksQueryKeys.detail(activeWorkspaceKey, task.id),
            });
        },
    });
}

export function useSetTaskThreadLastReadAtCache() {
    const queryClient = useQueryClient();
    const { activeWorkspaceKey } = useScope();

    return (threadId: string, lastReadAt: number) => {
        let matchedTaskId: string | undefined;

        queryClient.setQueriesData(
            {
                queryKey: tasksQueryKeys.listByWorkspaceKey(activeWorkspaceKey),
                exact: false,
            },
            (oldData: Task[] | undefined) => {
                const readTask = oldData?.find(
                    (task) => task.thread?.id === threadId,
                );
                if (!oldData || !readTask?.thread) {
                    return;
                }
                matchedTaskId = readTask.id;
                if (!isThreadUnread(readTask.thread)) {
                    return;
                }
                const readThread = {
                    ...readTask.thread,
                    lastReadAt: new Date(lastReadAt),
                };
                return oldData.map((task) =>
                    task === readTask ? { ...task, thread: readThread } : task,
                );
            },
        );

        if (!matchedTaskId) {
            const detailEntries = queryClient.getQueriesData<Task>({
                queryKey:
                    tasksQueryKeys.detailByWorkspaceKey(activeWorkspaceKey),
                exact: false,
            });
            for (const [, task] of detailEntries) {
                if (task?.thread?.id === threadId) {
                    matchedTaskId = task.id;
                    break;
                }
            }
        }

        if (!matchedTaskId) {
            return;
        }

        const readTaskId = matchedTaskId;
        queryClient.setQueriesData(
            {
                queryKey:
                    tasksQueryKeys.threadUnreadCountsByWorkspaceKey(
                        activeWorkspaceKey,
                    ),
                exact: false,
            },
            (oldData: TaskThreadUnreadCount[] | undefined) => {
                const hasUnreadCount = oldData?.some(
                    (count) =>
                        count.taskId === readTaskId && count.unreadCount > 0,
                );
                if (!oldData || !hasUnreadCount) {
                    return;
                }
                return oldData.map((count) =>
                    count.taskId === readTaskId
                        ? { ...count, unreadCount: 0 }
                        : count,
                );
            },
        );
    };
}

export function useStickyRefreshTask(workspaceId: string, taskId: string) {
    const queryClient = useQueryClient();
    const { activeWorkspaceKey } = useScope();

    return useMutation({
        mutationFn: async () => {
            const res = await taskClient.stickyRefresh({
                params: { workspaceId, taskId },
                body: {},
            });
            if (res.status !== 200) {
                return {
                    addedCount: 0,
                    transactionIds: undefined,
                    counterpartiesIds: undefined,
                    billIds: undefined,
                    financialDocumentIds: undefined,
                };
            }
            const {
                addedCount,
                transactionIds,
                counterpartiesIds,
                billIds,
                financialDocumentIds,
            } = res.body;

            queryClient.setQueryData(
                tasksQueryKeys.detail(activeWorkspaceKey, taskId),
                (oldData: Task | undefined) => {
                    if (!oldData?.view) {
                        return oldData;
                    }
                    return {
                        ...oldData,
                        view: {
                            ...oldData.view,
                            filters: {
                                ...oldData.view.filters,
                                ...(transactionIds ? { transactionIds } : {}),
                                ...(counterpartiesIds
                                    ? { counterpartiesIds }
                                    : {}),
                                ...(billIds ? { billIds } : {}),
                                ...(financialDocumentIds
                                    ? { financialDocumentIds }
                                    : {}),
                            },
                        },
                    };
                },
            );
            queryClient.setQueriesData(
                {
                    queryKey:
                        tasksQueryKeys.listByWorkspaceKey(activeWorkspaceKey),
                    exact: false,
                },
                (oldData: Task[] | undefined) => {
                    if (!oldData) {
                        return oldData;
                    }
                    return oldData.map((existing) => {
                        if (existing.id !== taskId || !existing.view) {
                            return existing;
                        }
                        return {
                            ...existing,
                            view: {
                                ...existing.view,
                                filters: {
                                    ...existing.view.filters,
                                    ...(transactionIds
                                        ? { transactionIds }
                                        : {}),
                                    ...(counterpartiesIds
                                        ? { counterpartiesIds }
                                        : {}),
                                    ...(billIds ? { billIds } : {}),
                                    ...(financialDocumentIds
                                        ? { financialDocumentIds }
                                        : {}),
                                },
                            },
                        };
                    });
                },
            );

            return {
                addedCount,
                transactionIds,
                counterpartiesIds,
                billIds,
                financialDocumentIds,
            };
        },
    });
}

export const useRefetchAllTaskQueries = () => {
    const queryClient = useQueryClient();

    return async () => {
        await Promise.all([
            queryClient.refetchQueries({
                queryKey: tasksQueryKeys.all(),
                exact: false,
            }),
            queryClient.refetchQueries({
                queryKey: organizationTaskQueryKeys.all(),
            }),
        ]);
    };
};
